import { useFeatureDecision } from '@applyboard/ui-utils';
import CanadianVisaCalculatorLogo from '../../../assets/CanadianVisaCalculatorLogo.png';
import OneVascoBanner from '../../../assets/OneVascoBanner.png';
import USMockInterviewLogo from '../../../assets/USVisasMockInterviewLogo.png';
import { getApplyShopConfig, useGetProducts } from '../../../data/applyshopStore/v3/applyShopStore';
import { ApplyShopStoreProductWithVariants } from '../../../data/applyshopStore/v3/types';
import { Incentive, IncentiveServiceType } from '../../../data/incentives/useIncentives';
import { applyCardFilters } from '../../VasHub/ServiceListings/filterUtils';

type StudentVisaServiceCardsProps = {
  incentivesMap: Record<IncentiveServiceType, Incentive>;
  filters?: {
    nationality?: string;
    destination?: string[];
  };
};

export const useStudentVisaServiceCardData = ({
  incentivesMap,
  filters = {},
}: StudentVisaServiceCardsProps) => {
  const defaultCountry = '';
  const [decisionEngine, decisionEngineReady] = useFeatureDecision();
  const isNepalShopifyStoreEnabled =
    decisionEngine?.isFeatureEnabled('vas_nepal_shoppify_store') ?? false;

  const { products: visaServiceProducts, isProductsLoading } = useGetProducts({
    applyShopConfig: getApplyShopConfig(defaultCountry, isNepalShopifyStoreEnabled),
    collectionName: 'vfs-visa-services',
  });

  const vfsProduct = visaServiceProducts?.find((product: ApplyShopStoreProductWithVariants) =>
    product.handle.toLowerCase().includes('vfs-visa-services'),
  );
  const vfsCartLink = vfsProduct?.variants?.[0]?.applyShopCartLink ?? '';
  const isVFSDisabled = !vfsCartLink;

  const visaServiceCards = [
    {
      get cardKey() {
        return `${this.service}-${this.vendor}`;
      },
      service: 'visa',
      serviceLabel: 'Visa',
      vendor: 'one vasco',
      badge: incentivesMap['VisaServices']?.display_text,
      image: OneVascoBanner,
      description:
        'Get access to expert advisors who will check every supporting document before assisting you in drafting and submitting your visa application.',
      shareLinkButtonName: 'Share link',
      applyButtonName: 'Buy Now',
      applyButtonCTAUrl: vfsCartLink,
      vendorUrl: vfsCartLink,
      disabled: isVFSDisabled,
    },
    {
      get cardKey() {
        return `${this.service}-${this.vendor}`;
      },
      service: 'visa',
      serviceLabel: 'Visa',
      vendor: 'canadian visa calculator',
      badge: undefined,
      image: CanadianVisaCalculatorLogo,
      title: 'Canadian Visa Calculator',
      description:
        'Check your eligibility for a Canadian student visa by answering a few simple questions. Fast, easy, and personalized.',
      shareLinkButtonName: 'Share Link',
      applyButtonName: 'Try Now',
      applyButtonCTAUrl: 'https://www.applyboard.com/visa-calculator',
      vendorUrl: 'https://www.applyboard.com/visa-calculator',
      disabled: false,
    },
    {
      get cardKey() {
        return `${this.service}-${this.vendor}`;
      },
      service: 'visa',
      serviceLabel: 'Visa',
      vendor: 'us mock interview',
      badge: undefined,
      image: USMockInterviewLogo,
      title: 'US Visa Mock Interview',
      description:
        'Practice your US visa interview with AI-powered mock sessions to boost your confidence and reduce rejection risk.',
      shareLinkButtonName: 'Share Link',
      applyButtonName: 'Try Now',
      applyButtonCTAUrl: 'https://careers.interviewer.ai/Ai/63d6ca4a-f3d4-4522-9eed-c9b089671e73',
      vendorUrl: 'https://careers.interviewer.ai/Ai/63d6ca4a-f3d4-4522-9eed-c9b089671e73',
      disabled: false,
    },
  ];

  const filteredCards = applyCardFilters(visaServiceCards, 'visa', filters);

  return {
    cardsData: filteredCards,
    isLoading: isProductsLoading || !decisionEngineReady,
  };
};

export default useStudentVisaServiceCardData;
